import { createTheme, ThemeProvider } from "@mui/material";
import React from "react";
import NavBar from "./Component/NavBar/NavBar";
import "virtual:color-tokens.css";
import "./css/Layout.css";
import { useAuth } from "./API/auth";
import { fetchCurrentEnvironment } from "./API/agromanager/agromanager";
import { useUserData } from "./Component/AuthGuard/useUserData";
import { colors } from "./theme/colors";

type LayoutProps = {
  children: React.ReactNode;
};

export const theme = createTheme({
  palette: {
    primary: {
      main: colors.primary,
      dark: colors.primaryHover,
      contrastText: colors.onPrimary,
    },
    secondary: {
      main: colors.textMuted,
      dark: colors.link,
      light: colors.borderStrong,
      contrastText: colors.surface,
    },
    text: {
      primary: colors.text,
      secondary: colors.textMuted,
    },
    divider: colors.border,
    warning: {
      main: colors.warningFill,
      contrastText: colors.onWarning,
    },
    error: {
      main: colors.danger,
    },
    info: {
      main: colors.info,
    },
    success: {
      main: colors.success,
    },
  },
  typography: {
    fontFamily: "exo2, sans-serif",
  },
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        contained: {
          textTransform: "none",
          "&.MuiButton-loading.MuiButton-containedPrimary": {
            color: colors.onPrimary,
            backgroundColor: colors.primary,
          },
        },
        text: {
          color: colors.link,
          backgroundColor: "transparent",
          "&:hover": {
            color: colors.linkHover,
            backgroundColor: "transparent",
          },
          "&.MuiButton-loading": {
            color: colors.link,
          },
        },
        outlined: {
          color: colors.link,
          backgroundColor: colors.surfaceMuted,
          border: `solid 1px ${colors.borderStrong}`,
          "&:hover": {
            color: colors.link,
            borderColor: colors.borderStrong,
            backgroundColor: colors.surfaceSelected,
          },
          "&.MuiButton-loading": {
            color: colors.link,
            borderColor: colors.borderStrong,
            backgroundColor: colors.surfaceMuted,
          },
        },
        loadingIndicator: {
          color: "inherit",
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardError: {
          backgroundColor: colors.dangerSurface,
          color: colors.text,
          "& .MuiAlert-icon": { color: colors.danger },
        },
        standardWarning: {
          backgroundColor: colors.warningSurface,
          color: colors.text,
          "& .MuiAlert-icon": { color: colors.warningIcon },
        },
        filledWarning: {
          backgroundColor: colors.warningFill,
          color: colors.onWarning,
          "& .MuiAlert-icon": { color: colors.onWarning },
        },
        standardInfo: {
          backgroundColor: colors.infoSurface,
          color: colors.text,
          "& .MuiAlert-icon": { color: colors.info },
        },
        standardSuccess: {
          backgroundColor: colors.successSurface,
          color: colors.text,
          "& .MuiAlert-icon": { color: colors.success },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: colors.surface,
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: colors.borderStrong,
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          backgroundColor: colors.surface,
          "&.Mui-checked, &.MuiCheckbox-indeterminate": {
            color: colors.controlChecked,
          },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          "&.Mui-checked": {
            color: colors.controlChecked,
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          "&.Mui-checked": {
            color: colors.controlChecked,
          },
          "&.Mui-checked + .MuiSwitch-track": {
            backgroundColor: colors.controlChecked,
          },
        },
      },
    },
    MuiLink: {
      defaultProps: {
        underline: "none",
      },
      styleOverrides: {
        root: {
          color: colors.link,
          "&:hover": {
            color: colors.linkHover,
          },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: "0 24px 20px",
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: {
          backgroundColor: colors.surface,
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          backgroundColor: colors.surface,
          "&:hover": {
            backgroundColor: colors.surfaceMuted,
          },
        },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: colors.surfaceMuted,
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          "&.Mui-selected": {
            backgroundColor: colors.surfaceSelected,
          },
        },
      },
    },
  },
});

export default function Layout({ children }: LayoutProps) {
  const { isAgromanagerUser } = useUserData();
  const { setAgroManagerEnv, accessToken } = useAuth();

  React.useEffect(() => {
    if (isAgromanagerUser && accessToken) {
      fetchCurrentEnvironment().then((env) => {
        setAgroManagerEnv(env);
      });
    }
  }, [isAgromanagerUser, accessToken]);

  return (
    <ThemeProvider theme={theme}>
      <div className="app-root">{children}</div>
    </ThemeProvider>
  );
}

export function Splash({ children }: LayoutProps): React.ReactNode {
  return (
    <>
      <NavBar hasDrawer={false} />
      <div className="splash">
        <div className="splash-inner">{children}</div>
      </div>
    </>
  );
}
